<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="overview-container">
  <article class="api-mesh-article">
    <header class="article-header">
      <div class="article-lead">
        <p><b>API Mesh</b> - distributed integration layer that unifies access to multiple REST APIs, enabling centralized governance, orchestration, and simplified consumption.</p>
        <p class="highlight">It functions as a smart reverse proxy that routes, transforms, and secures API traffic across services, clouds, and tenants.</p>
      </div>
    </header>

    <!-- Problem Statement Section -->
    <section class="article-section">
      <h2 class="section-title">Problem Statement</h2>
      <div class="problem-content">
        <p>Initially, every product in the Knowledge Hub had to integrate directly with all others and exchange data. As the ecosystem grew, this became unsustainable: too many connections appeared, each application had knowledge of all others, and the system became overly complex.</p>
        <p>When a new product was added, it required two-way integrations with all existing products, creating a dense network of links that exponentially increased complexity and significantly slowed down implementation.</p>
      </div>
    </section>

    <!-- Solution Section -->
    <section class="article-section">
      <h2 class="section-title">Solution: Athena</h2>
      <div class="solution-content">
        <p><b>Athena</b> is a central integration platform that simplifies communication between applications. Each product connects to Athena once, defines which APIs to expose, and gains secure, reliable access to other applications.</p>
        <p>By replacing the "each-to-each" integration model with a hub-and-spoke architecture, Athena dramatically reduces the number of connections, lowers integration effort, simplifies maintenance, and makes the system scalable and predictable.</p>
        <p>Athena also manages security and monitoring, ensuring safe and consistent interactions across the ecosystem. This allows teams to focus on building features instead of managing complex integrations.</p>
      </div>
    </section>

    <section class="article-section">
      <h2 class="section-title">Core Capabilities</h2>

      <!-- Desktop Table View -->
      <div class="capabilities-table desktop-only">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Capability</th>
              <th>Description</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="capability-name">
                <strong>Unified API Access</strong>
              </td>
              <td>Exposes a single endpoint that aggregates multiple REST services.</td>
              <td class="example-code">
                <code>GET /job-summary/{{ '{' }}id{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">Aggregates responses from <code>/jobs/{{ '{' }}id{{ '}' }}</code>, <code>/customers/{{ '{' }}id{{ '}' }}</code>, and <code>/equipment/{{ '{' }}id{{ '}' }}</code> into one payload.</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Dynamic Routing</strong>
              </td>
              <td>Routes requests based on path, method, tenant, or metadata.</td>
              <td class="example-code">
                <code>GET /schedule/{{ '{' }}region{{ '}' }}/{{ '{' }}techId{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">Routes to different backend clusters based on <code>{{ '{' }}region{{ '}' }}</code> (e.g., <code>/eu/schedule</code>, <code>/us/schedule</code>).</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Policy Enforcement</strong>
              </td>
              <td>Applies authentication, rate limits, and access control centrally.</td>
              <td class="example-code">
                <code>POST /maintenance-report</code>
                <span class="arrow">→</span>
                <span class="description">API Mesh validates JWT roles; only users with role = <em>technician</em> can submit reports.</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Data Transformation</strong>
              </td>
              <td>Reshapes payloads between services and consumers.</td>
              <td class="example-code">
                <code>GET /legacy-thermostat/{{ '{' }}id{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">Mesh converts XML from a legacy system to JSON before returning to client.</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Multi-cloud Governance</strong>
              </td>
              <td>Manages APIs across AWS, Azure, and GCP with consistent rules.</td>
              <td class="example-code">
                <code>GET /inventory/{{ '{' }}sku{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">API Mesh routes to AWS-hosted inventory service or Azure-hosted backup based on availability.</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Tenant Isolation</strong>
              </td>
              <td>Ensures secure, scoped access for partners or business units.</td>
              <td class="example-code">
                <code>GET /client-data/{{ '{' }}tenantId{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">Mesh injects tenant-specific filters so commercial clients see only their own data.</span>
              </td>
            </tr>
            <tr>
              <td class="capability-name">
                <strong>Legacy Integration</strong>
              </td>
              <td>Bridges old systems with modern APIs using adapters and endpoint abstraction.</td>
              <td class="example-code">
                <code>GET /building-control/{{ '{' }}id{{ '}' }}</code>
                <span class="arrow">→</span>
                <span class="description">Mesh wraps a SOAP service behind a RESTful adapter,exposing it as a clean REST endpoint.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="capabilities-cards mobile-only">
        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Unified API Access</h3>
          </div>
          <p class="capability-description">Exposes a single endpoint that aggregates multiple REST services.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /job-summary/{{ '{' }}id{{ '}' }}</code>
            </div>
            <div class="example-description">
              Aggregates responses from <code>/jobs/{{ '{' }}id{{ '}' }}</code>, <code>/customers/{{ '{' }}id{{ '}' }}</code>, and <code>/equipment/{{ '{' }}id{{ '}' }}</code> into one payload.
            </div>
          </div>
        </div>

        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Dynamic Routing</h3>
          </div>
          <p class="capability-description">Routes requests based on path, method, tenant, or metadata.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /schedule/{{ '{' }}region{{ '}' }}/{{ '{' }}techId{{ '}' }}</code>
            </div>
            <div class="example-description">
              Routes to different backend clusters based on <code>{{ '{' }}region{{ '}' }}</code> (e.g., <code>/eu/schedule</code>, <code>/us/schedule</code>).
            </div>
          </div>
        </div>

        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Policy Enforcement</h3>
          </div>
          <p class="capability-description">Applies authentication, rate limits, and access control centrally.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>POST /maintenance-report</code>
            </div>
            <div class="example-description">
              API Mesh validates JWT roles; only users with role = <em>technician</em> can submit reports.
            </div>
          </div>
        </div>

        <div class="capability-card">
            <div class="capability-header">
            <h3 class="capability-title">Data Transformation</h3>
          </div>
          <p class="capability-description">Reshapes payloads between services and consumers.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /legacy-thermostat/{{ '{' }}id{{ '}' }}</code>
            </div>
            <div class="example-description">
              Mesh converts XML from a legacy system to JSON before returning to client.
            </div>
          </div>
        </div>

        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Multi-cloud Governance</h3>
          </div>
          <p class="capability-description">Manages APIs across AWS, Azure, and GCP with consistent rules.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /inventory/{{ '{' }}sku{{ '}' }}</code>
            </div>
            <div class="example-description">
              API Mesh routes to AWS-hosted inventory service or Azure-hosted backup based on availability.
            </div>
          </div>
        </div>

        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Tenant Isolation</h3>
          </div>
          <p class="capability-description">Ensures secure, scoped access for partners or business units.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /client-data/{{ '{' }}tenantId{{ '}' }}</code>
            </div>
            <div class="example-description">
              Mesh injects tenant-specific filters so commercial clients see only their own data.
            </div>
          </div>
        </div>

        <div class="capability-card">
          <div class="capability-header">
            <h3 class="capability-title">Legacy Integration</h3>
          </div>
          <p class="capability-description">Bridges old systems with modern APIs using adapters and endpoint abstraction.</p>
          <div class="capability-example">
            <div class="example-title">Example:</div>
            <div class="example-code">
              <code>GET /building-control/{{ '{' }}id{{ '}' }}</code>
            </div>
            <div class="example-description">
              Mesh wraps a SOAP service behind a RESTful adapter, exposing it as a clean REST endpoint.
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Business Value Section -->
    <section class="article-section">
      <h2 class="section-title">Business Value</h2>

      <!-- Desktop Table View -->
      <div class="business-value-table desktop-only">
        <table class="modern-table">
          <thead>
            <tr>
              <th>Value Area</th>
              <th>Benefits</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="value-title">
                <strong>Simplified Service-to-Service Communication</strong>
              </td>
              <td>
                <p>Athena supports decentralized data ownership, where each team manages its own product independently, while Athena ensures secure and consistent connections between them.</p>
              </td>
            </tr>
            <tr>
              <td class="value-title">
                <strong>Faster Time-to-Market</strong>
              </td>
              <td>
                <p>New applications can be easily connected through Athena, without the need for complex integrations. This reduces the time teams spend on technical setup, allows them to focus on delivering features, and enables a faster time-to-market with fewer errors.</p>
                <p><b>Two-step integration process:</b></p>
                <ul>
                  <li>Integrate the application with Athena to gain access to others.</li>
                  <li>Decide which APIs to expose through Athena and which to keep private.</li>
                </ul>
                <p>Streamlined integration through standardized integration tools (e.g., NuGet package) reduces development effort and accelerates product delivery.</p>
              </td>
            </tr>
            <tr>
              <td class="value-title">
                <strong>Enhanced Security</strong>
              </td>
              <td>
                <p>Every exchange between applications is protected. Access control protects data and prevents unauthorized access, reducing compliance risks and safeguarding customer trust.</p>
              </td>
            </tr>
            <tr>
              <td class="value-title">
                <strong>Centralized Control and Monitoring</strong>
              </td>
              <td>
                <p><b>Traffic Management:</b> Athena manages traffic, errors, policies, and access in one place, ensuring service stability, simplifying monitoring, and providing consistent data availability across platforms.</p>
                <p><b>Visibility:</b> Single dashboard for metrics, logs, and traces, making it easier to detect and resolve issues, reducing downtime.</p>
                <p><b>Reliability:</b> Retries, timeouts, and circuit breakers reduce outages and enhance stability.</p>
                <p><b>Authorization:</b> Centralized authorization process that grants access to APIs from all applications integrated with Athena.</p>
              </td>
            </tr>
            <tr>
              <td class="value-title">
                <strong>Efficiency and Cost Savings</strong>
              </td>
              <td>
                <p>Achieved by minimizing manual work and reducing outages, which lowers team effort and operational costs.</p>
                <p>Simplified data retrieval from various platforms offers a consistent and efficient way to access information, making interactions easier.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="business-value-cards mobile-only">
        <div class="value-card">
          <div class="value-header">
            <h3 class="value-title">Simplified Service-to-Service Communication</h3>
          </div>
          <p class="value-description">Athena supports decentralized data ownership, where each team manages its own product independently, while Athena ensures secure and consistent connections between them.</p>
        </div>

        <div class="value-card">
          <div class="value-header">
            <h3 class="value-title">Faster Time-to-Market</h3>
          </div>
          <p class="value-description">New applications can be easily connected through Athena, without the need for complex integrations. This reduces the time teams spend on technical setup and allows them to focus on delivering features.</p>
          <div class="value-details">
            <p><b>Two-step integration process:</b></p>
            <ul>
              <li>Integrate the application with Athena to gain access to others.</li>
              <li>Decide which APIs to expose through Athena and which to keep private.</li>
            </ul>
            <p>Streamlined integration through standardized integration tools (e.g., NuGet package) reduces development effort and accelerates product delivery.</p>
          </div>
        </div>

        <div class="value-card">
          <div class="value-header">
            <h3 class="value-title">Enhanced Security</h3>
          </div>
          <p class="value-description">Every exchange between applications is protected. Access control protects data and prevents unauthorized access, reducing compliance risks and safeguarding customer trust.</p>
        </div>

        <div class="value-card">
          <div class="value-header">
            <h3 class="value-title">Centralized Control and Monitoring</h3>
          </div>
          <div class="value-details">
            <p><b>Traffic Management:</b> Athena manages traffic, errors, policies, and access in one place.</p>
            <p><b>Visibility:</b> Single dashboard for metrics, logs, and traces.</p>
            <p><b>Reliability:</b> Retries, timeouts, and circuit breakers reduce outages.</p>
            <p><b>Authorization:</b> Centralized authorization process for all integrated applications.</p>
          </div>
        </div>

        <div class="value-card">
          <div class="value-header">
            <h3 class="value-title">Efficiency and Cost Savings</h3>
          </div>
          <p class="value-description">Minimizes manual work and reduces outages, lowering team effort and operational costs. Simplified data retrieval from various platforms offers consistent access to information.</p>
        </div>
      </div>
    </section>
  </article>
</div>
